import { type FormEvent, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Logo } from '../components/brand/Logo';
import { Spinner } from '../components/ui/spinner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import * as api from '../lib/api';
import { useAuthStore } from '../store/auth';

/** Cloudflare Turnstile — explicit render. Defaults to the "always passes" TEST
 * site key so the flow works before real keys are set. */
const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '1x00000000000000000000AA';

interface Turnstile {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  reset: (id?: string) => void;
  remove: (id: string) => void;
}
declare global {
  interface Window {
    turnstile?: Turnstile;
  }
}

function loadTurnstile(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.turnstile) return resolve();
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${TURNSTILE_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('load failed')));
      return;
    }
    const s = document.createElement('script');
    s.src = TURNSTILE_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('load failed'));
    document.head.appendChild(s);
  });
}

type Phase = 'checking' | 'gate' | 'sending' | 'sent';

/**
 * Public "Try the live sandbox" entry (Feature 24). Resume-by-cookie: a returning
 * visitor whose live session is *itself a sandbox* is dropped straight back in —
 * but a logged-in real customer always sees the gate so they can still start a
 * sandbox. Submitting the gate emails a single-use link (email must be real);
 * clicking it (`/try/verify`) provisions the sandbox and signs in.
 */
export function TrySandboxPage() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>('checking');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState('');
  const widgetRef = useRef<HTMLDivElement>(null);
  const checked = useRef(false);

  // Resume only when the existing session is itself a sandbox; otherwise (logged
  // out, or logged in as a real customer) show the gate so a sandbox can start.
  useEffect(() => {
    if (checked.current) return;
    checked.current = true;
    void (async () => {
      try {
        const existing = await api.refreshSession();
        if (existing && existing.user.organizationKind === 'SANDBOX') {
          useAuthStore.setState({ status: 'authenticated', user: existing.user });
          navigate('/', { replace: true });
          return;
        }
      } catch {
        /* fall through to the gate */
      }
      setPhase('gate');
    })();
  }, [navigate]);

  // Render the Turnstile widget once the gate is shown.
  useEffect(() => {
    if (phase !== 'gate') return;
    let widgetId: string | undefined;
    let cancelled = false;
    loadTurnstile()
      .then(() => {
        if (cancelled || !widgetRef.current || !window.turnstile) return;
        widgetId = window.turnstile.render(widgetRef.current, {
          sitekey: SITE_KEY,
          callback: (t: string) => setToken(t),
          'error-callback': () => setToken(null),
          'expired-callback': () => setToken(null),
          theme: 'dark',
        });
      })
      .catch(() => setError('The bot check failed to load — please refresh and try again.'));
    return () => {
      cancelled = true;
      if (widgetId && window.turnstile) {
        try {
          window.turnstile.remove(widgetId);
        } catch {
          /* ignore */
        }
      }
    };
  }, [phase]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!token) {
      setError('Please complete the “I’m not a bot” check.');
      return;
    }
    setPhase('sending');
    try {
      const receipt = await api.requestSandboxLink({
        email,
        name: name.trim() || undefined,
        company: company.trim() || undefined,
        turnstileToken: token,
      });
      setSentTo(receipt.email);
      setPhase('sent');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Couldn’t send your sandbox link — please try again.',
      );
      setPhase('gate');
      setToken(null);
      window.turnstile?.reset();
    }
  }

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center gap-6 px-6 py-16">
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10"
        style={{
          background: 'radial-gradient(55% 45% at 50% 0%, var(--iris-dim), transparent 70%)',
        }}
      />
      <Logo size={34} />

      {phase === 'checking' || phase === 'sending' ? (
        <div className="flex flex-col items-center gap-3">
          <Spinner />
          <p className="text-sm text-muted">
            {phase === 'sending' ? 'Sending your sandbox link…' : 'One moment…'}
          </p>
        </div>
      ) : phase === 'sent' ? (
        <div className="w-full max-w-sm text-center">
          <h1 className="font-display text-2xl font-bold text-bone">Check your email</h1>
          <p className="mt-2 text-sm text-muted">
            We sent a sandbox link to <span className="font-medium text-bone">{sentTo}</span>. It
            opens your private demo, works once, and expires in 3 days.
          </p>
          <p className="mt-4 text-xs text-muted">
            Didn’t get it? Check spam, or{' '}
            <button
              type="button"
              onClick={() => {
                setPhase('gate');
                setToken(null);
                setError(null);
              }}
              className="text-iris hover:text-iris-strong"
            >
              use a different email
            </button>
            .
          </p>
          <div className="mt-6 flex items-center justify-center gap-4 border-t border-hairline pt-4 text-sm">
            <Link to="/login" className="text-iris hover:text-iris-strong">
              Sign in
            </Link>
            <span className="text-muted" aria-hidden>
              ·
            </span>
            <Link to="/register" className="text-iris hover:text-iris-strong">
              Create an account
            </Link>
          </div>
        </div>
      ) : (
        <div className="w-full max-w-sm">
          <div className="text-center">
            <h1 className="font-display text-2xl font-bold text-bone">Enter the live sandbox</h1>
            <p className="mt-2 text-sm text-muted">
              A private, fully-interactive demo on sample data — no signup, nothing to connect.
            </p>
          </div>

          <form
            onSubmit={onSubmit}
            className="mt-6 space-y-3 rounded-lg border border-hairline bg-surface p-6"
          >
            <Input
              type="email"
              required
              placeholder="Work email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <div className="grid grid-cols-2 gap-3">
              <Input
                type="text"
                placeholder="Name (optional)"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <Input
                type="text"
                placeholder="Company (optional)"
                autoComplete="organization"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
              />
            </div>

            {/* Turnstile widget renders here */}
            <div ref={widgetRef} className="flex justify-center" />

            {error && <p className="text-sm text-danger">{error}</p>}

            <Button type="submit" disabled={!token} className="w-full">
              Enter the sandbox →
            </Button>
            <p className="text-center text-xs text-muted">
              We’ll only use this to see who’s trying Lizrd — no marketing emails, no ads.
            </p>
          </form>
        </div>
      )}
    </div>
  );
}
